import { z } from "zod"
import { hexToHslChannels } from "@/utils/color"
import { normalizeRelayUrl } from "@/utils/url"
import { DEFAULT_CONFIG } from "./defaults"
import { deepMerge } from "./merge"
import type { AppConfig } from "./types"

const hexColor = z
  .string()
  .max(32)
  .refine((value) => hexToHslChannels(value) !== undefined, "expected a hex colour such as #7c62c8")

const relayUrl = z
  .string()
  .max(300)
  .refine((value) => normalizeRelayUrl(value) !== undefined, "expected a ws:// or wss:// relay URL")

const iconName = z.string().max(40)
const themeName = z.enum(["light", "dark", "pure-black"])

const brandingSchema = z
  .object({
    name: z.string().min(1).max(80),
    shortName: z.string().min(1).max(40),
    description: z.string().max(400),
    logo: z.string().max(600),
    favicon: z.string().max(600),
    titleTemplate: z.string().max(160),
    footerText: z.string().max(300),
    links: z
      .array(
        z.object({
          label: z.string().min(1).max(80),
          url: z.string().max(600),
          icon: iconName.optional(),
        }),
      )
      .max(20),
  })
  .partial()

const designSchema = z
  .object({
    colors: z
      .object({
        primary: hexColor,
        accent: hexColor,
      })
      .partial(),
    theme: z
      .object({
        default: z.enum(["light", "dark", "pure-black", "system"]),
        allowed: z.array(themeName).min(1).max(3),
      })
      .partial(),
    radius: z.number().min(0).max(32),
    density: z.enum(["compact", "comfortable", "spacious"]),
    fontScale: z.number().min(0.75).max(1.5),
    fontFamily: z.string().max(300),
  })
  .partial()

const featuresSchema = z
  .object({
    explore: z.boolean(),
    relayReviews: z.boolean(),
    notifications: z.boolean(),
    bookmarks: z.boolean(),
    search: z.boolean(),
    composer: z.boolean(),
    relaySets: z.boolean(),
    trending: z.boolean(),
    imageUploads: z.boolean(),
  })
  .partial()

const relayOptionsSchema = z
  .object({
    connectTimeout: z.number().min(1000).max(60_000),
    publishTimeout: z.number().min(1000).max(60_000),
    maxReconnectAttempts: z.number().min(0).max(50),
    reconnectBaseDelay: z.number().min(100).max(60_000),
    verifyEvents: z.boolean(),
    eoseTimeout: z.number().min(500).max(30_000),
    relayInfoTimeout: z.number().min(1000).max(60_000),
    infoRefreshMs: z.number().min(60_000).max(30 * 24 * 3600 * 1000),
  })
  .partial()

const relaysSchema = z
  .object({
    defaultRelays: z.array(relayUrl).min(1).max(60),
    searchRelays: z.array(relayUrl).max(30),
    trendingRelays: z.array(relayUrl).max(30),
    discoveryRelays: z.array(relayUrl).max(60),
    discoveryCollectionsUrl: z.string().max(600),
    relayOptions: relayOptionsSchema,
  })
  .partial()

const navigationSchema = z
  .object({
    items: z
      .array(
        z.object({
          id: z.string().min(1).max(40),
          label: z.string().max(60).optional(),
          icon: iconName,
          path: z.string().regex(/^\//, "navigation paths must start with /"),
          visible: z.boolean(),
        }),
      )
      .max(20),
  })
  .partial()

const i18nSchema = z
  .object({
    defaultLanguage: z.string().min(2).max(12),
    languages: z.array(z.string().min(2).max(12)).min(1).max(20),
  })
  .partial()

const communitySchema = z
  .object({
    mode: z.boolean(),
    relaySets: z
      .array(
        z.object({
          id: z.string().min(1).max(80),
          name: z.string().min(1).max(80),
          relayUrls: z.array(relayUrl).min(1).max(60),
        }),
      )
      .max(30),
    extraRelays: z.array(relayUrl).max(60),
  })
  .partial()

const SECTION_SCHEMAS = {
  branding: brandingSchema,
  design: designSchema,
  features: featuresSchema,
  relays: relaysSchema,
  navigation: navigationSchema,
  i18n: i18nSchema,
  community: communitySchema,
} as const

export type ConfigSection = keyof typeof SECTION_SCHEMAS

export interface ConfigPatchResult {
  patch: Record<string, unknown>
  warnings: string[]
}

function issuesMessage(error: z.ZodError): string {
  const issue = error.issues[0]
  if (!issue) return "invalid value"
  const path = issue.path.length > 0 ? issue.path.join(".") : "value"
  return `${path}: ${issue.message}`
}

/**
 * Validates one configuration layer.
 *
 * A layer is never rejected as a whole: valid keys are kept, invalid keys are
 * dropped with a warning, so a typo in a runtime file cannot break the app.
 */
export function validateConfigPatch(raw: unknown, source: string): ConfigPatchResult {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return { patch: {}, warnings: [`${source}: expected a JSON object`] }
  }

  const input = raw as Record<string, unknown>
  const patch: Record<string, unknown> = {}
  const warnings: string[] = []

  for (const [section, schema] of Object.entries(SECTION_SCHEMAS)) {
    const value = input[section]
    if (value === undefined) continue

    const parsed = schema.safeParse(value)
    if (parsed.success) {
      patch[section] = parsed.data
      continue
    }

    if (value === null || typeof value !== "object" || Array.isArray(value)) {
      warnings.push(`${source}: ignored section "${section}" (${issuesMessage(parsed.error)})`)
      continue
    }

    const shape = schema.shape as Record<string, z.ZodType>
    const salvaged: Record<string, unknown> = {}
    for (const [key, keySchema] of Object.entries(shape)) {
      const rawKey = (value as Record<string, unknown>)[key]
      if (rawKey === undefined) continue
      const keyParsed = keySchema.safeParse(rawKey)
      if (keyParsed.success) {
        salvaged[key] = keyParsed.data
      } else {
        warnings.push(`${source}: ignored ${section}.${key} (${issuesMessage(keyParsed.error)})`)
      }
    }
    patch[section] = salvaged
  }

  const known = new Set([...Object.keys(SECTION_SCHEMAS), "version", "$comment", "$schema"])
  for (const key of Object.keys(input)) {
    if (!known.has(key)) warnings.push(`${source}: unknown configuration key "${key}"`)
  }

  return { patch, warnings }
}

/** Applies a validated patch on top of a base configuration. */
export function applyConfigPatch(base: AppConfig, patch: Record<string, unknown>): AppConfig {
  return finalizeConfig(deepMerge(base, patch))
}

/** Guarantees the invariants the rest of the application relies on. */
export function finalizeConfig(config: AppConfig): AppConfig {
  if (!Array.isArray(config.relays.defaultRelays) || config.relays.defaultRelays.length === 0) {
    config.relays.defaultRelays = [...DEFAULT_CONFIG.relays.defaultRelays]
  }
  const allowedThemes = config.design.theme.allowed
  if (!allowedThemes.includes(config.design.theme.default as (typeof allowedThemes)[number])) {
    if (config.design.theme.default !== "system") {
      config.design.theme.default = allowedThemes[0]
    }
  }
  if (!Array.isArray(config.navigation.items) || config.navigation.items.length === 0) {
    config.navigation.items = DEFAULT_CONFIG.navigation.items.map((item) => ({ ...item }))
  }
  if (!config.i18n.languages.includes(config.i18n.defaultLanguage)) {
    config.i18n.languages = [config.i18n.defaultLanguage, ...config.i18n.languages]
  }
  return config
}
